import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { AppRoutes } from './app/app-routes';
import { applyTheme, rememberedTheme } from './app/theme-memory';
import { watchDeployedBuild } from './core/version/version-check';
import './styles/fonts.css';
import './styles/tailwind.css';

/*
 * The appearance, before anything renders. `/theme.js`, loaded by `index.html`, has normally
 * applied it already — this is the authority, and that one is there so the page a returning
 * visitor chose dark is not painted white first.
 */
applyTheme(rememberedTheme());

const container = document.getElementById('root');

if (container === null) {
    throw new Error('The #root element is missing from index.html.');
}

const app = (
    <StrictMode>
        <BrowserRouter>
            <AppRoutes />
        </BrowserRouter>
    </StrictMode>
);

/*
 * Hydrate what the build wrote, or mount from nothing — decided by what is in the element rather
 * than by the URL, which is the only test that cannot fall out of step with nginx.
 *
 * The two cases are the product's own split. A content page — the landing, the legal document, the
 * booking wizard, the blog — is written to a file at build time with its markup already in it, so
 * it arrives readable to a crawler, a link preview and a visitor on a slow connection, and this
 * adopts that markup instead of discarding it. The portal has no build-time form at all: it is one
 * customer's records, fetched with their session, and nginx serves it the empty shell from the SPA
 * fallback. An empty `#root` is precisely that shell, so it mounts client-side.
 *
 * `hydrateRoot` on an empty element would render the whole tree and warn about every node of it;
 * `createRoot` over prerendered markup would throw that markup away and paint the page twice. The
 * check is one line and it is the difference between them.
 */
if (container.firstChild === null) {
    createRoot(container).render(app);
} else {
    hydrateRoot(container, app);
}

/*
 * After mounting, never before: the check may end in a reload, and one that fires while the page is
 * still painting throws away a render the visitor was about to see for a deployment that can just
 * as well be picked up a moment later.
 */
watchDeployedBuild();
